import {
  useEffect,
  useState,
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { hex2rgb, type HSL, type RGB } from './colorMath.js';

export type ColorPickerFormat = 'hex' | 'rgb' | 'hsl';

export interface ColorPickerInputsProps {
  hex: string;
  rgb: RGB;
  hsl: HSL;
  alpha: boolean;
  defaultFormat: ColorPickerFormat;
  onCommitHex: (s: string) => void;
  onCommitRgb: (rgb: RGB) => void;
  onCommitHsl: (hsl: HSL) => void;
}

const FORMATS: ReadonlyArray<{ id: ColorPickerFormat; label: string }> = [
  { id: 'hex', label: 'HEX' },
  { id: 'rgb', label: 'RGB' },
  { id: 'hsl', label: 'HSL' },
];

/** Format-switchable input row: tab strip on top (HEX | RGB | HSL), the
 *  matching input(s) underneath. Hex stretches; RGB / HSL split into
 *  three (or four with alpha) tabular-numeric cells. Lives in its own
 *  file so the main ColorPicker stays well inside the 500-line budget. */
export function ColorPickerInputs(props: ColorPickerInputsProps): ReactNode {
  const { hex, rgb, hsl, alpha, defaultFormat, onCommitHex, onCommitRgb, onCommitHsl } = props;
  const [format, setFormat] = useState<ColorPickerFormat>(defaultFormat);

  return (
    <div className="vq-color-picker__inputs" data-slot="inputs">
      <div className="vq-color-picker__format-tabs" role="tablist" aria-label="Colour format">
        {FORMATS.map((f) => (
          <button
            key={f.id}
            type="button"
            role="tab"
            aria-selected={format === f.id}
            tabIndex={format === f.id ? 0 : -1}
            className="vq-color-picker__format-tab"
            data-active={format === f.id ? 'true' : 'false'}
            onClick={() => {
              setFormat(f.id);
            }}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div className="vq-color-picker__input-row" data-format={format}>
        {format === 'hex' && <HexInput hex={hex} onCommit={onCommitHex} />}
        {format === 'rgb' && <RgbInputs rgb={rgb} alpha={alpha} onCommit={onCommitRgb} />}
        {format === 'hsl' && <HslInputs hsl={hsl} alpha={alpha} onCommit={onCommitHsl} />}
      </div>
    </div>
  );
}

interface HexInputProps {
  hex: string;
  onCommit: (s: string) => void;
}

function HexInput(props: HexInputProps): ReactNode {
  const { hex, onCommit } = props;
  // The user types the body of the hex without the `#` prefix; the
  // visible draft mirrors that, while we hand a fully-prefixed string to
  // onCommit so callers see consistent values.
  const [draft, setDraft] = useState(hex.replace(/^#/, ''));
  const [valid, setValid] = useState(true);
  useEffect(() => {
    setDraft(hex.replace(/^#/, ''));
    setValid(true);
  }, [hex]);
  const apply = (body: string): void => {
    setDraft(body);
    const candidate = `#${body}`;
    const ok = hex2rgb(candidate) !== null;
    setValid(ok);
    if (ok) onCommit(candidate);
  };
  const onChange = (e: ChangeEvent<HTMLInputElement>): void => {
    apply(e.target.value);
  };
  const onKey = (e: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      const candidate = `#${draft}`;
      const ok = hex2rgb(candidate) !== null;
      if (ok) onCommit(candidate);
      else {
        setDraft(hex.replace(/^#/, ''));
        setValid(true);
      }
    }
  };
  const onBlur = (): void => {
    if (hex2rgb(`#${draft}`) === null) {
      setDraft(hex.replace(/^#/, ''));
      setValid(true);
    }
  };
  return (
    <label className="vq-color-picker__input-cell vq-color-picker__input-cell--hex">
      <span className="vq-color-picker__input-prefix" aria-hidden="true">
        #
      </span>
      <input
        type="text"
        className="vq-color-picker__input"
        value={draft}
        onChange={onChange}
        onKeyDown={onKey}
        onBlur={onBlur}
        spellCheck={false}
        autoComplete="off"
        aria-label="Hex value"
        aria-invalid={!valid}
        data-valid={valid ? 'true' : 'false'}
      />
    </label>
  );
}

interface RgbInputsProps {
  rgb: RGB;
  alpha: boolean;
  onCommit: (rgb: RGB) => void;
}

function RgbInputs(props: RgbInputsProps): ReactNode {
  const { rgb, alpha, onCommit } = props;
  const set = (channel: 'r' | 'g' | 'b' | 'a') => (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') return;
    const n = parseFloat(raw);
    if (Number.isNaN(n)) return;
    if (channel === 'a') {
      onCommit({ ...rgb, a: Math.max(0, Math.min(1, n)) });
    } else {
      onCommit({ ...rgb, [channel]: Math.max(0, Math.min(255, Math.round(n))) });
    }
  };
  return (
    <>
      <NumericCell label="R" value={rgb.r} min={0} max={255} step={1} onChange={set('r')} />
      <NumericCell label="G" value={rgb.g} min={0} max={255} step={1} onChange={set('g')} />
      <NumericCell label="B" value={rgb.b} min={0} max={255} step={1} onChange={set('b')} />
      {alpha && (
        <NumericCell
          label="A"
          value={Number(rgb.a.toFixed(2))}
          min={0}
          max={1}
          step={0.01}
          onChange={set('a')}
        />
      )}
    </>
  );
}

interface HslInputsProps {
  hsl: HSL;
  alpha: boolean;
  onCommit: (hsl: HSL) => void;
}

function HslInputs(props: HslInputsProps): ReactNode {
  const { hsl, alpha, onCommit } = props;
  const set = (channel: 'h' | 's' | 'l' | 'a') => (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') return;
    const n = parseFloat(raw);
    if (Number.isNaN(n)) return;
    if (channel === 'h') {
      onCommit({ ...hsl, h: ((Math.round(n) % 360) + 360) % 360 });
    } else if (channel === 'a') {
      onCommit({ ...hsl, a: Math.max(0, Math.min(1, n)) });
    } else {
      onCommit({ ...hsl, [channel]: Math.max(0, Math.min(100, Math.round(n))) / 100 });
    }
  };
  return (
    <>
      <NumericCell
        label="H"
        value={Math.round(hsl.h)}
        min={0}
        max={360}
        step={1}
        onChange={set('h')}
      />
      <NumericCell
        label="S"
        value={Math.round(hsl.s * 100)}
        min={0}
        max={100}
        step={1}
        suffix="%"
        onChange={set('s')}
      />
      <NumericCell
        label="L"
        value={Math.round(hsl.l * 100)}
        min={0}
        max={100}
        step={1}
        suffix="%"
        onChange={set('l')}
      />
      {alpha && (
        <NumericCell
          label="A"
          value={Number(hsl.a.toFixed(2))}
          min={0}
          max={1}
          step={0.01}
          onChange={set('a')}
        />
      )}
    </>
  );
}

interface NumericCellProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  suffix?: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

function NumericCell(props: NumericCellProps): ReactNode {
  const { label, value, min, max, step, suffix, onChange } = props;
  return (
    <label className="vq-color-picker__input-cell">
      <span className="vq-color-picker__input-label">{label}</span>
      <span className="vq-color-picker__input-wrap">
        <input
          type="number"
          className="vq-color-picker__input"
          value={value}
          min={min}
          max={max}
          step={step}
          inputMode={step < 1 ? 'decimal' : 'numeric'}
          aria-label={label}
          onChange={onChange}
        />
        {suffix !== undefined && (
          <span className="vq-color-picker__input-suffix" aria-hidden="true">
            {suffix}
          </span>
        )}
      </span>
    </label>
  );
}
